import { useCallback, useReducer } from "react";
import { GridData } from "../../components/ag-grid-extensions/models/GridData";
import { GridApi } from "ag-grid-community";
import { actions } from "./AlarmSetpointReportActions";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { ActionMenuItem } from "../../components/action-menu/ActionMenuItem";
import { sortAndDispatchActions } from "../../common/utilities/SortAndDispatchActions";

export type AlarmSetpointReportStateType = {

    viewOptionState: ViewOptionStateEnum;
    organizationID: number | undefined;
    data: GridData | undefined;
    gridApi: GridApi | undefined;
    downloadUid: string | undefined;
    actions: ActionMenuItem[];
    includeInActiveAndInvisibleDevices: boolean;
};

const defaultAlarmSetpointReportState: AlarmSetpointReportStateType = {
    viewOptionState: ViewOptionStateEnum.Default,
    organizationID: undefined,
    data: undefined,
    gridApi: undefined,
    downloadUid: undefined,
    actions: actions,
    includeInActiveAndInvisibleDevices: false
};

export {defaultAlarmSetpointReportState}

enum AlarmSetpointReportStateActionType {
    OrganizationChanged = 'SETPOINT_ORGANIZATION_CHANGED',
    DataChanged = 'SETPOINT_REPORT_DATA_CHANGED',
    DownloadUidChanged = 'SETPOINT_REPORT_DOWNLOAD_UID_CHANGED',
    GridApiChanged = 'SETPOINT_REPORT_GRID_API_CHANGED',
    ActionsChanged = 'SETPOINT_REPORT_ACTIONS_CHANGED',
    IncludeInActiveChanged = 'SETPOINT_REPORT_INCLUDE_INACTIVE_CHANGED',
    ResetChanged = 'SETPOINT_REPORT_RESET_CHANGED'


}

type AlarmSetpointReportStateActions = | {
    type: AlarmSetpointReportStateActionType.DataChanged;
    payload: GridData;
} | {
    type: AlarmSetpointReportStateActionType.GridApiChanged;
    payload: GridApi;
} | {
    type: AlarmSetpointReportStateActionType.OrganizationChanged;
    payload: number | undefined;
} | {
    type: AlarmSetpointReportStateActionType.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: AlarmSetpointReportStateActionType.ActionsChanged;
    payload: ActionMenuItem[];
} | {    
    type: AlarmSetpointReportStateActionType.IncludeInActiveChanged;
    payload: boolean;
} | {    
    type: AlarmSetpointReportStateActionType.ResetChanged;
    payload: void;
};


function alarmSetpointReportStateReducer(state: AlarmSetpointReportStateType, 
    action: AlarmSetpointReportStateActions): AlarmSetpointReportStateType {

    const copy = {...state};
    switch (action.type) {
        
        case AlarmSetpointReportStateActionType.OrganizationChanged:
            copy.organizationID = action.payload;
            return copy;

        case AlarmSetpointReportStateActionType.DataChanged:
            copy.data = action.payload;
            return copy;

        case AlarmSetpointReportStateActionType.GridApiChanged:
            copy.gridApi = action.payload;
            return copy;

   
        case AlarmSetpointReportStateActionType.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;

        case AlarmSetpointReportStateActionType.ActionsChanged:
            copy.actions = action.payload;
            return copy;

        case AlarmSetpointReportStateActionType.IncludeInActiveChanged:
            copy.viewOptionState = action.payload !== defaultAlarmSetpointReportState.includeInActiveAndInvisibleDevices ? 
                                        ViewOptionStateEnum.Changed : 
                                        ViewOptionStateEnum.Default;
            copy.includeInActiveAndInvisibleDevices = action.payload;
            return copy;
    

        case AlarmSetpointReportStateActionType.ResetChanged:
            return {...defaultAlarmSetpointReportState};
    
    
        default:
            return state;
    }
}

export default function useAlarmSetpointReportState() {

    const [state, dispatch] = useReducer(alarmSetpointReportStateReducer, {...defaultAlarmSetpointReportState});

    const setData = useCallback((value: GridData) => dispatch({
        type: AlarmSetpointReportStateActionType.DataChanged,
        payload: value
    }), []);

    const setGridApi = useCallback((value: GridApi) => dispatch({
        type: AlarmSetpointReportStateActionType.GridApiChanged,
        payload: value
    }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: AlarmSetpointReportStateActionType.DownloadUidChanged,
        payload: value
    }), []);

    const setOrganization = useCallback((value: number | undefined) => dispatch({
        type: AlarmSetpointReportStateActionType.OrganizationChanged,
        payload: value
    }), []);

    // const setActions = useCallback((value: ActionMenuItem[]) => dispatch({
    //     type: AlarmSetpointReportStateActionType.ActionsChanged,
    //     payload: value
    // }), []);

    const setActions = useCallback((actions: ActionMenuItem[]) => sortAndDispatchActions(
        state.actions,
        actions,
        (sortedActions) => dispatch({
            type: AlarmSetpointReportStateActionType.ActionsChanged,
            payload: sortedActions
        })
    ), [state.actions]);

    const setIncludeInActive = useCallback((value: boolean) => dispatch({
        type: AlarmSetpointReportStateActionType.IncludeInActiveChanged,
        payload: value
    }), []);

    const resetState = useCallback(() => dispatch({
        type: AlarmSetpointReportStateActionType.ResetChanged,
        payload: undefined
    }), []);


    return {
        state,
        setData,
        setGridApi,
        setDownloadUid,
        setOrganization,
        setActions,
        setIncludeInActive,
        resetState
    };
}

